import { useState, useEffect } from "react";
import { Button } from "../button";
import { ButtonIntent } from "../button/button.styles";
import { cn } from "../../lib/cn";
import { CheckStatusModal } from "../check-status-modal";

import INSTAGRAM from "../../images/new-design/icons/instagram.svg";
import FACEBOOK from "../../images/new-design/icons/facebook.svg";
import FACEBOOK_WHITE from "../../images/new-design/icons/facebook_white.svg";
import INSTAGRAM_WHITE from "../../images/new-design/icons/instagram_white.svg";

function Navbar({ whiteLabel = false }: { whiteLabel?: boolean }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show sticky nav after scrolling past the navbar (approximately 100-150px)
      setIsScrolled(currentScrollY > 100);

      // Show navbar when scrolling up, hide when scrolling down
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setShowNavbar(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setShowNavbar(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <>
      {/* Sticky Navigation for Mobile (appears after scroll) */}
      <div
        className={cn([
          "fixed top-0 left-0 right-0 z-50 bg-white shadow-md transition-transform duration-300 lg:hidden h-[70px]",
          isScrolled ? "translate-y-0" : "-translate-y-full",
        ])}
      >
        <div className="flex items-center justify-center relative px-4 h-full">
          <img
            src="/navbar/logo.svg"
            className="h-6 w-auto"
            alt="League Life Logo"
            style={{ filter: "brightness(0)" }}
          />
          <button
            className="flex flex-col gap-1.5 p-2 absolute right-4"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            <span
              className={cn([
                "block w-6 h-0.5 bg-black transition-all duration-300",
                isMenuOpen && "rotate-45 translate-y-2",
              ])}
            />
            <span
              className={cn([
                "block w-6 h-0.5 bg-black transition-all duration-300",
                isMenuOpen && "opacity-0",
              ])}
            />
            <span
              className={cn([
                "block w-6 h-0.5 bg-black transition-all duration-300",
                isMenuOpen && "-rotate-45 -translate-y-2",
              ])}
            />
          </button>
        </div>
      </div>

      {/* Sticky Navigation for Desktop (appears on scroll up) */}
      <div
        className={cn([
          "fixed top-0 left-0 right-0 z-50 bg-white shadow-md transition-transform duration-300 hidden lg:block",
          isScrolled && showNavbar ? "translate-y-0" : "-translate-y-full",
        ])}
      >
        <div className="flex items-center justify-between px-8 py-4 max-w-[1440px] mx-auto relative">
          {/* Left section - Social icons */}
          <div className="flex gap-5 items-center">
            <a
              href="https://www.instagram.com/ourleaguelife"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Instagram"
            >
              <Button
                intent={ButtonIntent.Icon}
                size={ButtonIntent.Icon}
                className="w-12 h-12"
              >
                <img alt="instagram" src={INSTAGRAM} className="w-6 h-6" />
              </Button>
            </a>

            <a
              href="https://www.facebook.com/share/1aRXjjWYFf/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Facebook"
            >
              <Button
                intent={ButtonIntent.Icon}
                size={ButtonIntent.Icon}
                className="w-12 h-12"
              >
                <img alt="facebook" src={FACEBOOK} className="w-6 h-6" />
              </Button>
            </a>
          </div>

          {/* Center section - Logo (centered to page) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex justify-center items-center">
            <img
              src="/navbar/logo.svg"
              className="w-48 h-auto"
              alt="League Life Logo"
              style={{ filter: "brightness(0)" }}
            />
          </div>

          {/* Right section - Buttons */}
          <div className="flex gap-2 items-center">
            <a
              href="https://abi38wtuwac.typeform.com/to/Q3gHPxSM"
              target="_blank"
              rel="noopener noreferrer"
              className="m-0 p-0 w-auto h-auto"
            >
              <Button intent={ButtonIntent.Black}>Request access</Button>
            </a>
            <Button onClick={() => setIsModalOpen(true)}>
              Check your status
            </Button>
          </div>
        </div>
      </div>

      {/* Original Navigation */}
      <div className="flex items-center justify-between relative">
        {/* Left section - Social icons (desktop only) */}
        <div className={cn(["gap-5 hidden items-center", "lg:flex"])}>
          <a
            href="https://www.instagram.com/ourleaguelife"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow us on Instagram"
          >
            <Button
              intent={ButtonIntent.Icon}
              size={ButtonIntent.Icon}
              className={cn([whiteLabel && "bg-black hover:bg-black"])}
            >
              <img
                alt="instagram"
                src={whiteLabel ? INSTAGRAM_WHITE : INSTAGRAM}
              />
            </Button>
          </a>

          <a
            href="https://www.facebook.com/share/1aRXjjWYFf/?mibextid=wwXIfr"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow us on Facebook"
          >
            <Button
              intent={ButtonIntent.Icon}
              size={ButtonIntent.Icon}
              className={cn([whiteLabel && "bg-black hover:bg-black"])}
            >
              <img
                alt="facebook"
                src={whiteLabel ? FACEBOOK_WHITE : FACEBOOK}
              />
            </Button>
          </a>
        </div>

        {/* Center section - Logo (always centered to screen) */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex justify-center items-center">
          <img
            src="/navbar/logo.svg"
            className="lg:w-64 h-auto w-48"
            alt="League Life Logo"
            style={whiteLabel ? { filter: "brightness(0)" } : undefined}
          />
        </div>

        {/* Right section - Buttons (desktop) / Hamburger (mobile) */}
        <div className="ml-auto z-10">
          {/* Desktop buttons */}
          <div className={cn(["gap-2 hidden items-center", "lg:flex"])}>
            <a
              href="https://abi38wtuwac.typeform.com/to/Q3gHPxSM"
              target="_blank"
              rel="noopener noreferrer"
              className="m-0 p-0 w-auto h-auto"
            >
              <Button intent={ButtonIntent.Black}>Request access</Button>
            </a>
            <Button onClick={() => setIsModalOpen(true)}>
              Check your status
            </Button>
          </div>

          {/* Mobile hamburger menu */}
          <button
            className="lg:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={cn([
                "block w-6 h-0.5 transition-all duration-300",
                whiteLabel ? "bg-black" : "bg-white",
                isMenuOpen && "rotate-45 translate-y-2",
              ])}
            />
            <span
              className={cn([
                "block w-6 h-0.5 transition-all duration-300",
                whiteLabel ? "bg-black" : "bg-white",
                isMenuOpen && "opacity-0",
              ])}
            />
            <span
              className={cn([
                "block w-6 h-0.5 transition-all duration-300",
                whiteLabel ? "bg-black" : "bg-white",
                isMenuOpen && "-rotate-45 -translate-y-2",
              ])}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay - Full Screen */}
      <div
        className={cn([
          "fixed inset-0 bg-white transform transition-transform duration-300 ease-in-out z-50 lg:hidden",
          isMenuOpen ? "translate-x-0" : "translate-x-full",
        ])}
      >
        {/* Close button */}
        <button
          className="absolute top-6 right-6 text-black"
          onClick={() => setIsMenuOpen(false)}
          aria-label="Close menu"
        >
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Menu content - Centered vertically */}
        <div className="flex flex-col items-center justify-center h-full px-8 gap-8">
          {/* Logo */}
          <img
            src="/footer/league-life-logo.png"
            className="w-24 h-auto mb-4"
            alt="League Life Logo"
          />

          {/* App Store Button */}
          <a
            href="https://apps.apple.com/us/app/league-life/id1612547898"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-8 mx-auto"
          >
            <img
              src="/footer/applestore-logo.png"
              alt="Download on the App Store"
              className="w-48 h-auto mx-auto"
            />
          </a>

          {/* Buttons */}
          <div className="flex flex-col gap-4 w-full max-w-md items-center justify-center">
            <a
              href="https://abi38wtuwac.typeform.com/to/Q3gHPxSM"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMenuOpen(false)}
              className="w-[310px]"
            >
              <Button
                intent={ButtonIntent.Black}
                className="w-full max-w-[310px] font-black text-[20px] leading-[40%] py-4 h-[55px]"
              >
                REQUEST ACCESS
              </Button>
            </a>
            <Button
              onClick={() => {
                setIsMenuOpen(false);
                setIsModalOpen(true);
              }}
              className="w-full max-w-[310px] font-black text-[20px] leading-[40%] py-4 h-[55px] mt-3"
            >
              CHECK YOUR STATUS
            </Button>
          </div>

          {/* Social links */}
          <div className="flex flex-col gap-6 mt-12 items-center">
            <a
              href="https://www.instagram.com/ourleaguelife"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-black text-lg"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              <span>@ourleaguelife</span>
            </a>

            <a
              href="https://www.facebook.com/share/1aRXjjWYFf/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-black text-lg"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span>League Life</span>
            </a>

            <a
              href="https://tiktok.com/@theleaguelife"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-black text-lg"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
              </svg>
              <span>@theleaguelife</span>
            </a>
          </div>
        </div>
      </div>

      {/* Backdrop overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Check Status Modal */}
      <CheckStatusModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}

export { Navbar };
